import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

const STEPS = [
  {
    step: '01',
    label: 'Install the plugin',
    code: 'claude plugin install\n  https://github.com/BACC-Labs/cynthia-generator',
    caption: 'Free · open source · works locally in Claude Code',
  },
  {
    step: '02',
    label: 'Generate your first patients',
    code: '/generate --diagnosis "Type 2 Diabetes"\n         --count 10 --years 3',
    caption: 'Up to 100 records locally, no account needed',
  },
  {
    step: '03',
    label: 'Connect your API key for scale',
    code: '# Ready for more?\n# Set CYNTHIA_API_KEY to unlock cloud\nexport CYNTHIA_API_KEY=cy_••••••',
    caption: 'Free key — 5 cloud records/month. Credits from $1/rec',
  },
];

export default function PluginBridge() {
  return (
    <Box
      id="plugin"
      sx={{
        background: 'linear-gradient(180deg, #060e1e 0%, #071422 100%)',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'rgba(56,189,248,0.12)',
        py: { xs: 8, sm: 12 },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#38bdf8',
              fontFamily: 'monospace',
              letterSpacing: '0.12em',
              fontWeight: 700,
              fontSize: '0.72rem',
            }}
          >
            CLAUDE CODE PLUGIN
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#f0f9ff' }}>
            Start in your terminal. Scale in the cloud.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#94a3b8', maxWidth: 560, lineHeight: 1.7 }}
          >
            The Cynthia plugin runs entirely locally — no account, no API key, no data leaving your machine.
            When you need cloud scale, team access, or automated pipelines, one API key unlocks it all.
          </Typography>
        </Stack>

        {/* 3-step terminal flow */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
          }}
        >
          {STEPS.map((s, i) => (
            <Box
              key={s.step}
              sx={{
                position: 'relative',
                background: '#0a1628',
                border: '1px solid',
                borderColor: i === 2 ? 'rgba(56,189,248,0.35)' : 'rgba(30,58,95,0.8)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Terminal chrome */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 1,
                  background: 'rgba(255,255,255,0.03)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
                  <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.7 }} />
                ))}
                <Typography variant="caption" sx={{ color: '#475569', ml: 0.5, fontFamily: 'monospace', fontSize: '0.68rem' }}>
                  {s.label}
                </Typography>
              </Box>

              {/* Code */}
              <Box sx={{ px: 2, py: 2 }}>
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    lineHeight: 1.65,
                    color: '#e2e8f0',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {s.code.split('\n').map((line, li) => {
                    const isComment = line.trim().startsWith('#');
                    return (
                      <span
                        key={li}
                        style={{ color: isComment ? '#475569' : undefined, display: 'block' }}
                      >
                        {!isComment && (
                          <span style={{ color: '#38bdf8', userSelect: 'none' }}>$ </span>
                        )}
                        {line}
                      </span>
                    );
                  })}
                </Typography>
              </Box>

              {/* Caption */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="caption" sx={{ color: '#475569', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {s.caption}
                </Typography>
              </Box>

              {/* Step number watermark */}
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 14,
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: 'rgba(56,189,248,0.06)',
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                {s.step}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CTA row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
          <Button
            component="a"
            href="https://github.com/BACC-Labs/cynthia-generator"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="large"
            sx={{
              borderColor: 'rgba(56,189,248,0.35)',
              color: '#7dd3fc',
              fontFamily: 'monospace',
              '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(56,189,248,0.05)' },
            }}
          >
            View plugin on GitHub
          </Button>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{ fontWeight: 700 }}
          >
            Get free API key
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: '#334155', mt: 3 }}
        >
          Already have a key? Set <code style={{ color: '#475569' }}>CYNTHIA_API_KEY</code> in your shell and the plugin picks it up automatically.
        </Typography>
      </Container>
    </Box>
  );
}
